/**
 * Display layout presets for product subsections and catalog upsell chrome.
 * Shared by admin editor, preview bundle, and template refine scripts.
 */
import { normalizeRatingSectionDisplay } from "./rating-display.js";

export const PRICE_LAYOUT = {
  INLINE_ROW: "inline-row",
  PREMIUM_CARD_V2: "premium-card-v2",
  STACKED_COMPACT: "stacked-compact",
};

/** Legacy editor/export values → canonical layout ids */
const PRICE_LAYOUT_ALIASES = {
  "premium-card": PRICE_LAYOUT.PREMIUM_CARD_V2,
  "premium-card-v1": PRICE_LAYOUT.PREMIUM_CARD_V2,
  stacked: PRICE_LAYOUT.STACKED_COMPACT,
  "stacked-compact-v1": PRICE_LAYOUT.STACKED_COMPACT,
};

/**
 * @param {string | undefined} layout
 * @returns {string}
 */
export function normalizePriceLayout(layout) {
  const raw = String(layout || "").trim();
  if (!raw) return PRICE_LAYOUT.PREMIUM_CARD_V2;
  if (PRICE_LAYOUT_ALIASES[raw]) return PRICE_LAYOUT_ALIASES[raw];
  if (Object.values(PRICE_LAYOUT).includes(raw)) return raw;
  return PRICE_LAYOUT.PREMIUM_CARD_V2;
}

export const VARIANT_PICKER_STYLE = {
  /** @deprecated Legacy id — normalized to SLIDER at read time */
  TABS: "tabs-v1",
  DROPDOWN: "dropdown-v1",
  SLIDER: "slider-v1",
  PREMIUM_DROPDOWN: "premium-dropdown-v1",
};

/** Legacy editor/export values → canonical picker ids */
const VARIANT_PICKER_STYLE_ALIASES = {
  [VARIANT_PICKER_STYLE.TABS]: VARIANT_PICKER_STYLE.SLIDER,
};

/**
 * @param {string | undefined} style
 * @returns {string}
 */
export function normalizeVariantPickerStyle(style) {
  const raw = String(style || "").trim();
  if (!raw) return VARIANT_PICKER_STYLE.SLIDER;
  if (VARIANT_PICKER_STYLE_ALIASES[raw]) return VARIANT_PICKER_STYLE_ALIASES[raw];
  if (Object.values(VARIANT_PICKER_STYLE).includes(raw)) {
    return raw === VARIANT_PICKER_STYLE.TABS
      ? VARIANT_PICKER_STYLE.SLIDER
      : raw;
  }
  return VARIANT_PICKER_STYLE.SLIDER;
}

export const SOCIAL_PROOF_STYLE = {
  AVATARS_BAR: "avatars-bar-v1",
  SPLIT_METRICS: "split-metrics-v1",
  INLINE_PILLS: "inline-pills-v1",
  EMPHASIS_ROW: "emphasis-row-v1",
  TEXT_ONLY: "text-only-v1",
};

/** Legacy editor/export values → canonical v1 ids */
const SOCIAL_PROOF_STYLE_ALIASES = {
  "split-metrics": SOCIAL_PROOF_STYLE.SPLIT_METRICS,
  "avatars-bar": SOCIAL_PROOF_STYLE.AVATARS_BAR,
  "inline-pills": SOCIAL_PROOF_STYLE.INLINE_PILLS,
  "emphasis-row": SOCIAL_PROOF_STYLE.EMPHASIS_ROW,
  "text-only": SOCIAL_PROOF_STYLE.TEXT_ONLY,
};

/**
 * @param {string | undefined} style
 * @returns {string}
 */
export function normalizeSocialProofStyle(style) {
  const raw = String(style || "").trim();
  if (!raw) return SOCIAL_PROOF_STYLE.SPLIT_METRICS;
  if (SOCIAL_PROOF_STYLE_ALIASES[raw]) return SOCIAL_PROOF_STYLE_ALIASES[raw];
  if (Object.values(SOCIAL_PROOF_STYLE).includes(raw)) return raw;
  return SOCIAL_PROOF_STYLE.SPLIT_METRICS;
}

export const SOCIAL_PROOF_ACCENT = {
  WARM: "warm",
  MINT: "mint",
  VIOLET: "violet",
};

export const TRUST_BADGES_STYLE = {
  PILL_STRIP: "pill-strip-v1",
  INLINE_ROW: "inline-row-v1",
  CARD_TRIO: "card-trio-v1",
  ICON_GRID: "icon-grid-v1",
  TEXT_ONLY: "text-only-v1",
};

export const TRUST_BADGES_ACCENT = {
  NEUTRAL: "neutral",
  MINT: "mint",
  SLATE: "slate",
};

export const TRUST_ICON_PRESET = {
  SHIELD: "shield",
  TRUCK: "truck",
  LOCK: "lock",
  STAR: "star",
  CHECK: "check",
  HEART: "heart",
};

/** @type {Record<string, string>} */
export const TRUST_ICON_EMOJI = {
  [TRUST_ICON_PRESET.SHIELD]: "🛡",
  [TRUST_ICON_PRESET.TRUCK]: "🚚",
  [TRUST_ICON_PRESET.LOCK]: "🔒",
  [TRUST_ICON_PRESET.STAR]: "⭐",
  [TRUST_ICON_PRESET.CHECK]: "✓",
  [TRUST_ICON_PRESET.HEART]: "♥",
};

export const DEFAULT_TRUST_BADGE_ITEMS = [
  { id: "returns", icon: TRUST_ICON_PRESET.SHIELD, label: "30-day returns", enabled: true },
  { id: "shipping", icon: TRUST_ICON_PRESET.TRUCK, label: "Free shipping", enabled: true },
  { id: "secure", icon: TRUST_ICON_PRESET.LOCK, label: "Secure checkout", enabled: true },
];

export const BUTTON_CTA_VARIANT = {
  FILLED_DARK: "filled-dark",
  OUTLINE_LIGHT: "outline-light",
  OUTLINE_MUTED: "outline-muted",
};

export const SAVE_PILL_MODE = {
  PERCENT: "percent",
  AMOUNT: "amount",
  PERCENT_AND_AMOUNT: "percent_and_amount",
};

export const DEFAULT_PRICE_DISPLAY = {
  layout: PRICE_LAYOUT.PREMIUM_CARD_V2,
  showCompareAt: true,
  showSavePill: true,
  savePillMode: SAVE_PILL_MODE.PERCENT_AND_AMOUNT,
  showSubLabel: true,
  subLabel: "One-time · No subscription",
  compareAtFontSize: "12px",
  compareAtColor: "#64748b",
  /** Offer badge (save pill) design — used when showSavePill is true */
  savePillBackground: "#ecfdf5",
  savePillColor: "#065f46",
  savePillFontSize: "12px",
  savePillBorderWidth: "0px",
  savePillBorderColor: "transparent",
  savePillBorderRadius: "999px",
};

export const QUANTITY_LAYOUT = {
  STEPPER_BAR: "stepper-bar",
  LABELED_ROW: "labeled-row",
  COMPACT: "compact",
  PREMIUM_COMPACT: "premium-compact-v1",
};

export const DEFAULT_VARIANT_PICKER_DISPLAY = {
  label: "",
  variantPickerVariant: VARIANT_PICKER_STYLE.SLIDER,
};

export const DEFAULT_QUANTITY_DISPLAY = {
  layout: QUANTITY_LAYOUT.COMPACT,
  label: "",
  showIncentiveBadge: false,
  incentiveText: "Buy 2 → Extra 10% off",
};

/** Editor card metadata (icons are emoji for lightweight previews). */
export const PRICE_LAYOUT_CARDS = [
  {
    id: PRICE_LAYOUT.PREMIUM_CARD_V2,
    title: "Premium card",
    description: "Bordered card with compare-at and save pill",
    icon: "💳",
    preview: "premium-card",
  },
  {
    id: PRICE_LAYOUT.INLINE_ROW,
    title: "Inline row",
    description: "Price, compare-at, and pill in one row",
    icon: "↔️",
    preview: "inline-row",
  },
  {
    id: PRICE_LAYOUT.STACKED_COMPACT,
    title: "Stacked",
    description: "Vertical stack for narrow layouts",
    icon: "▤",
    preview: "stacked",
  },
];

export const VARIANT_PICKER_STYLE_CARDS = [
  {
    id: VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN,
    title: "Premium dropdown",
    description: "Compact select aligned with recommendation cards",
    icon: "✦",
    preview: "premium-dropdown",
  },
  {
    id: VARIANT_PICKER_STYLE.DROPDOWN,
    title: "Dropdown",
    description: "Single select with search",
    icon: "▾",
    preview: "dropdown",
  },
  {
    id: VARIANT_PICKER_STYLE.SLIDER,
    title: "Slider",
    description: "Scrollable pills when you have many variants",
    icon: "⇄",
    preview: "slider",
  },
];

export const QUANTITY_LAYOUT_CARDS = [
  {
    id: QUANTITY_LAYOUT.PREMIUM_COMPACT,
    title: "Premium compact",
    description: "Full-width stepper for recommendation cards",
    icon: "✦",
    preview: "premium-compact",
  },
  {
    id: QUANTITY_LAYOUT.LABELED_ROW,
    title: "Label + stepper",
    description: "Label, quantity control, optional incentive",
    icon: "🔢",
    preview: "labeled",
  },
  {
    id: QUANTITY_LAYOUT.STEPPER_BAR,
    title: "Stepper bar",
    description: "Full-width minus / plus bar",
    icon: "±",
    preview: "stepper",
  },
  {
    id: QUANTITY_LAYOUT.COMPACT,
    title: "Compact",
    description: "Minimal centered stepper",
    icon: "⊟",
    preview: "compact",
  },
];

export const DEFAULT_SOCIAL_PROOF_DISPLAY = {
  style: SOCIAL_PROOF_STYLE.SPLIT_METRICS,
  showLiveDot: true,
  showAvatars: true,
  avatarCount: 4,
  showViewingMetric: true,
  showPurchaseMetric: true,
  viewingCount: "47",
  viewingLabel: "people viewing this",
  purchaseCount: "12",
  purchaseLabel: "bought in the last hour",
  accent: SOCIAL_PROOF_ACCENT.WARM,
  showIcon: true,
};

export const SOCIAL_PROOF_STYLE_CARDS = [
  {
    id: SOCIAL_PROOF_STYLE.SPLIT_METRICS,
    title: "Split metrics",
    description: "Two highlighted stats side by side",
    icon: "📊",
    preview: "split-metrics",
  },
  {
    id: SOCIAL_PROOF_STYLE.AVATARS_BAR,
    title: "Avatars bar",
    description: "Live dot, faces, and activity line",
    icon: "👥",
    preview: "avatars-bar",
  },
  {
    id: SOCIAL_PROOF_STYLE.INLINE_PILLS,
    title: "Metric pills",
    description: "Compact pills for viewing and purchases",
    icon: "💊",
    preview: "inline-pills",
  },
  {
    id: SOCIAL_PROOF_STYLE.EMPHASIS_ROW,
    title: "Emphasis row",
    description: "Icon with bold numbers in one line",
    icon: "⚡",
    preview: "emphasis-row",
  },
  {
    id: SOCIAL_PROOF_STYLE.TEXT_ONLY,
    title: "Text only",
    description: "Simple centered message",
    icon: "📝",
    preview: "text-only",
  },
];

/**
 * Parse legacy single-line copy: "47 people viewing this · 12 bought in the last hour"
 * @param {string} text
 */
export function parseSocialProofText(text) {
  const raw = String(text || "").trim();
  if (!raw) {
    return { viewing: null, purchase: null };
  }
  const parts = raw.split("·").map((p) => p.trim()).filter(Boolean);
  const parsePart = (part) => {
    const match = part.match(/^(\d[\d,]*)\s+(.+)$/i);
    if (!match) return { count: "", label: part };
    return { count: match[1].replace(/,/g, ""), label: match[2].trim() };
  };
  return {
    viewing: parts[0] ? parsePart(parts[0]) : null,
    purchase: parts[1] ? parsePart(parts[1]) : null,
  };
}

/**
 * @param {object} display
 */
export function composeSocialProofText(display = {}) {
  const parts = [];
  if (display.showViewingMetric !== false && display.viewingCount) {
    const label = String(display.viewingLabel || "people viewing").trim();
    parts.push(`${display.viewingCount} ${label}`.trim());
  }
  if (display.showPurchaseMetric !== false && display.purchaseCount) {
    const label = String(display.purchaseLabel || "bought recently").trim();
    parts.push(`${display.purchaseCount} ${label}`.trim());
  }
  return parts.join(" · ");
}

/**
 * @param {object} section
 */
export function resolveSocialProofMetrics(section) {
  const display = getSocialProofDisplay(section);
  const hasStructured =
    display.viewingCount ||
    display.purchaseCount ||
    display.showViewingMetric !== undefined;

  if (hasStructured) {
    return {
      viewing:
        display.showViewingMetric !== false
          ? {
              count: String(display.viewingCount || "").trim(),
              label: String(display.viewingLabel || "people viewing this").trim(),
            }
          : null,
      purchase:
        display.showPurchaseMetric !== false
          ? {
              count: String(display.purchaseCount || "").trim(),
              label: String(display.purchaseLabel || "bought in the last hour").trim(),
            }
          : null,
    };
  }

  return parseSocialProofText(section?.text);
}

export const DEFAULT_TRUST_BADGES_DISPLAY = {
  style: TRUST_BADGES_STYLE.PILL_STRIP,
  showIcons: true,
  accent: TRUST_BADGES_ACCENT.NEUTRAL,
  items: DEFAULT_TRUST_BADGE_ITEMS.map((item) => ({ ...item })),
};

export const TRUST_BADGES_STYLE_CARDS = [
  {
    id: TRUST_BADGES_STYLE.PILL_STRIP,
    title: "Pill strip",
    description: "Rounded chips with icon and label",
    icon: "💊",
    preview: "trust-pills",
  },
  {
    id: TRUST_BADGES_STYLE.INLINE_ROW,
    title: "Inline row",
    description: "Centered row with icon + text",
    icon: "↔️",
    preview: "trust-inline",
  },
  {
    id: TRUST_BADGES_STYLE.CARD_TRIO,
    title: "Card trio",
    description: "Three mini trust cards",
    icon: "▦",
    preview: "trust-cards",
  },
  {
    id: TRUST_BADGES_STYLE.ICON_GRID,
    title: "Icon grid",
    description: "Icon above label in columns",
    icon: "⊞",
    preview: "trust-grid",
  },
  {
    id: TRUST_BADGES_STYLE.TEXT_ONLY,
    title: "Text only",
    description: "Simple line of guarantees",
    icon: "📝",
    preview: "trust-text",
  },
];

export const DEFAULT_BUTTON_DISPLAY = {
  variant: BUTTON_CTA_VARIANT.FILLED_DARK,
  fullWidth: true,
};

export const COVER_IMAGE_LAYOUT = {
  HORIZONTAL_SLIDER: "horizontal-slider-v1",
  HERO_THUMBNAILS: "hero-thumbnails-v1",
  /** One product image — used by compact checkout extension widgets */
  SINGLE: "single-v1",
};

/** Legacy editor/export values → canonical layout ids */
const COVER_IMAGE_LAYOUT_ALIASES = {
  "horizontal-slider": COVER_IMAGE_LAYOUT.HORIZONTAL_SLIDER,
  "hero-thumbnails": COVER_IMAGE_LAYOUT.HERO_THUMBNAILS,
  single: COVER_IMAGE_LAYOUT.SINGLE,
};

/**
 * @param {string | undefined} layout
 * @returns {string}
 */
export function normalizeCoverImageLayout(layout) {
  const raw = String(layout || "").trim();
  if (!raw) return COVER_IMAGE_LAYOUT.HERO_THUMBNAILS;
  if (COVER_IMAGE_LAYOUT_ALIASES[raw]) return COVER_IMAGE_LAYOUT_ALIASES[raw];
  if (Object.values(COVER_IMAGE_LAYOUT).includes(raw)) return raw;
  return COVER_IMAGE_LAYOUT.HERO_THUMBNAILS;
}

export const DEFAULT_COVER_BADGE_DISPLAY = {
  show: true,
  label: "BESTSELLER",
};

/** Catalog-flow column + hero/thumbnail sizes (px) for AI upsell product cards. */
export const DEFAULT_COVER_IMAGE_CATALOG_SIZES = Object.freeze({
  desktop: Object.freeze({
    columnWidth: 160,
    mainHeight: 168,
    thumbWidth: 44,
    thumbHeight: 44,
  }),
  mobile: Object.freeze({
    columnWidth: 108,
    mainHeight: 120,
    thumbWidth: 40,
    thumbHeight: 40,
  }),
});

function clampCatalogSize(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * @param {object} [section] COVER_IMAGE subsection
 * @param {boolean} [isMobile]
 */
export function getCoverImageCatalogSizes(section, isMobile = false) {
  const display = getCoverImageDisplay(section);
  const view = isMobile ? "mobile" : "desktop";
  const defaults = DEFAULT_COVER_IMAGE_CATALOG_SIZES[view];
  const custom =
    display.sizes?.[view] && typeof display.sizes[view] === "object"
      ? display.sizes[view]
      : {};

  if (display.autoImageHeight === true) {
    return {
      columnWidth: null,
      mainHeight: null,
      thumbWidth: clampCatalogSize(custom.thumbWidth, defaults.thumbWidth, 24, 96),
      thumbHeight: clampCatalogSize(custom.thumbHeight, defaults.thumbHeight, 24, 96),
    };
  }

  return {
    columnWidth: clampCatalogSize(custom.columnWidth, defaults.columnWidth, 72, 280),
    mainHeight: clampCatalogSize(custom.mainHeight, defaults.mainHeight, 64, 400),
    thumbWidth: clampCatalogSize(custom.thumbWidth, defaults.thumbWidth, 24, 96),
    thumbHeight: clampCatalogSize(custom.thumbHeight, defaults.thumbHeight, 24, 96),
  };
}

export const DEFAULT_COVER_IMAGE_DISPLAY = {
  layout: COVER_IMAGE_LAYOUT.HERO_THUMBNAILS,
  autoScroll: false,
  scrollIntervalMs: 4000,
  scrollSpeed: 32,
  gap: 8,
  maxThumbnailsVisible: 5,
  sizes: {
    desktop: { ...DEFAULT_COVER_IMAGE_CATALOG_SIZES.desktop },
    mobile: { ...DEFAULT_COVER_IMAGE_CATALOG_SIZES.mobile },
  },
};

export const COVER_IMAGE_LAYOUT_CARDS = [
  {
    id: COVER_IMAGE_LAYOUT.HERO_THUMBNAILS,
    title: "Hero + thumbnails",
    description: "Large image with selectable thumbnails below",
    icon: "🖼",
    preview: "cover-hero-thumbs",
  },
  {
    id: COVER_IMAGE_LAYOUT.HORIZONTAL_SLIDER,
    title: "Image strip",
    description: "Horizontal scroll row with optional auto-scroll",
    icon: "⇄",
    preview: "cover-horizontal-strip",
  },
];

function normalizeCoverImageUrl(src) {
  const raw = String(src || "").trim();
  if (!raw) return "";
  if (raw.startsWith("//")) {
    return `https:${raw}`;
  }
  return raw;
}

function stripCoverImageUrlQuery(url) {
  return String(url || "").split("?")[0];
}

/**
 * Resolve the image URL for a specific variant.
 * @param {object} [productData]
 * @param {object} [selectedVariant]
 * @returns {string}
 */
export function resolveVariantImageUrl(productData, selectedVariant) {
  if (!selectedVariant) return "";

  const images = Array.isArray(productData?.images) ? productData.images : [];

  if (selectedVariant.image_id != null && images.length) {
    const byId = images.find(
      (image) => String(image?.id) === String(selectedVariant.image_id)
    );
    if (byId?.src || byId?.url) {
      return normalizeCoverImageUrl(byId.src || byId.url);
    }
  }

  if (images.length) {
    const byVariantIds = images.find(
      (image) =>
        Array.isArray(image?.variant_ids) &&
        image.variant_ids.some(
          (variantId) => String(variantId) === String(selectedVariant.id)
        )
    );
    if (byVariantIds?.src || byVariantIds?.url) {
      return normalizeCoverImageUrl(byVariantIds.src || byVariantIds.url);
    }
  }

  const featured =
    selectedVariant?.featured_image?.src ||
    selectedVariant?.featured_image?.url ||
    selectedVariant?.image?.src ||
    selectedVariant?.image?.url ||
    selectedVariant?.image_url ||
    "";

  if (featured) return normalizeCoverImageUrl(featured);

  return normalizeCoverImageUrl(productData?.image?.src || productData?.image?.url) || "";
}

/**
 * Index of the selected variant image inside a resolved URL list.
 * @param {object} [productData]
 * @param {object} [selectedVariant]
 * @param {string[]} [urls]
 * @returns {number}
 */
export function getVariantImageIndex(productData, selectedVariant, urls = []) {
  const variantUrl = resolveVariantImageUrl(productData, selectedVariant);
  if (!variantUrl || !urls.length) return 0;

  const exact = urls.findIndex((url) => url === variantUrl);
  if (exact >= 0) return exact;

  const variantBase = stripCoverImageUrlQuery(variantUrl);
  const loose = urls.findIndex(
    (url) => stripCoverImageUrlQuery(url) === variantBase
  );
  if (loose >= 0) return loose;

  return 0;
}

/**
 * Resolve ordered image URLs for product cover blocks.
 * @param {object} [section]
 * @param {object} [productData]
 * @returns {string[]}
 */
export function resolveCoverImageUrls(section, productData) {
  const urls = [];
  const push = (src) => {
    const normalized = normalizeCoverImageUrl(src);
    if (!normalized || urls.includes(normalized)) return;
    urls.push(normalized);
  };

  if (Array.isArray(productData?.images) && productData.images.length) {
    for (const image of productData.images) {
      if (typeof image === "string") push(image);
      else push(image?.src || image?.url);
    }
  }

  if (Array.isArray(productData?.variants)) {
    for (const variant of productData.variants) {
      const variantUrl = resolveVariantImageUrl(productData, variant);
      if (variantUrl) push(variantUrl);
    }
  }

  if (!urls.length && Array.isArray(section?.src?.images)) {
    for (const src of section.src.images) push(src);
  }

  if (!urls.length && section?.src?.image) {
    push(section.src.image);
  }

  if (!urls.length) {
    push(productData?.image?.src || productData?.image?.url);
  }

  return urls;
}

/**
 * @param {object} section
 */
export function getCoverImageDisplay(section) {
  const raw = section?.setting?.display || {};
  const display = mergeDisplaySettings(raw, DEFAULT_COVER_IMAGE_DISPLAY);
  const autoImageHeight = raw.autoImageHeight === true;
  return {
    ...display,
    autoImageHeight,
    layout: normalizeCoverImageLayout(display.layout),
    badge: mergeDisplaySettings(raw.badge, DEFAULT_COVER_BADGE_DISPLAY),
    sizes: autoImageHeight
      ? {
          desktop:
            raw.sizes?.desktop && typeof raw.sizes.desktop === "object"
              ? raw.sizes.desktop
              : {},
          mobile:
            raw.sizes?.mobile && typeof raw.sizes.mobile === "object"
              ? raw.sizes.mobile
              : {},
        }
      : {
          desktop: mergeDisplaySettings(
            raw.sizes?.desktop,
            DEFAULT_COVER_IMAGE_CATALOG_SIZES.desktop
          ),
          mobile: mergeDisplaySettings(
            raw.sizes?.mobile,
            DEFAULT_COVER_IMAGE_CATALOG_SIZES.mobile
          ),
        },
  };
}

export const DEFAULT_RATING_DISPLAY = {
  showStars: true,
  ratingValue: "4.9",
  reviewCount: 2841,
};

export const PRODUCT_SUBSECTION_ROLE = {
  PRODUCT_TAGLINE: "productTagline",
};

export function mergeDisplaySettings(existing = {}, defaults = {}) {
  return { ...defaults, ...(existing && typeof existing === "object" ? existing : {}) };
}

export function getPriceDisplay(section) {
  const display = mergeDisplaySettings(section?.setting?.display, DEFAULT_PRICE_DISPLAY);
  return {
    ...display,
    layout: normalizePriceLayout(display.layout),
  };
}

/**
 * Merchant-facing variant picker label — only `setting.display.label` counts.
 * Legacy top-level `setting.label` (e.g. synced Shopify option names) is ignored.
 */
export function resolveVariantPickerLabel(section) {
  const raw = section?.setting?.display?.label;
  if (raw == null || raw === "") return "";
  return String(raw).trim();
}

export function getVariantPickerDisplay(section) {
  const displayObj = section?.setting?.display;
  const display = mergeDisplaySettings(
    {
      label: resolveVariantPickerLabel(section),
      variantPickerVariant:
        displayObj?.variantPickerVariant ?? section?.setting?.variantPickerVariant,
    },
    DEFAULT_VARIANT_PICKER_DISPLAY
  );
  return {
    ...display,
    variantPickerVariant: normalizeVariantPickerStyle(display.variantPickerVariant),
  };
}

export function getSocialProofDisplay(section) {
  const display = mergeDisplaySettings(
    section?.setting?.display,
    DEFAULT_SOCIAL_PROOF_DISPLAY
  );
  return {
    ...display,
    style: normalizeSocialProofStyle(display.style),
  };
}

/**
 * @param {string} text
 * @returns {{ id: string, icon: string, label: string, enabled: boolean }[]}
 */
export function parseTrustBadgesText(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const parts = raw.split(/[·|•]/).map((p) => p.trim()).filter(Boolean);
  const presets = DEFAULT_TRUST_BADGE_ITEMS;
  return parts.map((part, index) => {
    const preset =
      presets.find((p) => new RegExp(p.label.split(" ")[0], "i").test(part)) ||
      presets[index];
    let icon = preset?.icon || TRUST_ICON_PRESET.CHECK;
    if (/return/i.test(part)) icon = TRUST_ICON_PRESET.SHIELD;
    else if (/ship/i.test(part)) icon = TRUST_ICON_PRESET.TRUCK;
    else if (/secure|checkout/i.test(part)) icon = TRUST_ICON_PRESET.LOCK;
    return {
      id: preset?.id || `item-${index}`,
      icon,
      label: part,
      enabled: true,
    };
  });
}

/**
 * @param {object} display
 */
export function composeTrustBadgesText(display = {}) {
  const items = normalizeTrustBadgeItems(display.items);
  return items
    .filter((item) => item.enabled !== false && item.label)
    .map((item) => String(item.label).trim())
    .join("  ·  ");
}

/**
 * @param {unknown} raw
 */
export function normalizeTrustBadgeItems(raw) {
  if (!Array.isArray(raw) || !raw.length) {
    return DEFAULT_TRUST_BADGE_ITEMS.map((item) => ({ ...item }));
  }
  return raw.slice(0, 4).map((item, index) => ({
    id: item?.id || DEFAULT_TRUST_BADGE_ITEMS[index]?.id || `trust-${index}`,
    icon: item?.icon || TRUST_ICON_PRESET.CHECK,
    label:
      String(item?.label || "").trim() ||
      DEFAULT_TRUST_BADGE_ITEMS[index]?.label ||
      "Trust signal",
    enabled: item?.enabled !== false,
  }));
}

/**
 * @param {object} section
 */
export function resolveTrustBadgeItems(section) {
  const display = getTrustBadgesDisplay(section);
  if (
    Array.isArray(section?.setting?.display?.items) &&
    section.setting.display.items.length > 0
  ) {
    return normalizeTrustBadgeItems(display.items).filter(
      (item) => item.enabled !== false
    );
  }
  const parsed = parseTrustBadgesText(section?.text);
  if (parsed.length) return parsed;
  return normalizeTrustBadgeItems(display.items).filter(
    (item) => item.enabled !== false
  );
}

export function getTrustIconEmoji(iconKey) {
  return TRUST_ICON_EMOJI[iconKey] || TRUST_ICON_EMOJI[TRUST_ICON_PRESET.CHECK];
}

export function getTrustBadgesDisplay(section) {
  const display = mergeDisplaySettings(
    section?.setting?.display,
    DEFAULT_TRUST_BADGES_DISPLAY
  );
  display.items = normalizeTrustBadgeItems(display.items);
  return display;
}

export function getButtonDisplay(section) {
  return mergeDisplaySettings(section?.setting?.display, DEFAULT_BUTTON_DISPLAY);
}

export function getCoverBadgeDisplay(section) {
  const display = getCoverImageDisplay(section);
  return display.badge || DEFAULT_COVER_BADGE_DISPLAY;
}

export function getRatingDisplay(section) {
  const normalized = normalizeRatingSectionDisplay(section);
  return mergeDisplaySettings(normalized, DEFAULT_RATING_DISPLAY);
}

export function getQuantityDisplay(section) {
  const display = mergeDisplaySettings(
    section?.setting?.display,
    DEFAULT_QUANTITY_DISPLAY
  );
  if (
    display.layout === QUANTITY_LAYOUT.COMPACT ||
    display.layout === QUANTITY_LAYOUT.PREMIUM_COMPACT
  ) {
    return { ...display, label: "" };
  }
  return display;
}

export const PREMIUM_PRICE_HTML_INLINE = `<div class="price-container"><span class="price">{{ currency }} {{ discount_price }}</span><span class="compare-price">{{ currency }} {{ compare_at_price }}</span><span class="save-pill">Save {{ discount }}%</span></div>`;

export const PREMIUM_PRICE_OVERRIDE_CSS = {
  desktop: `.price-container{
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}
.price {
  font-weight: 700;
  font-size: 28px;
  letter-spacing: -0.03em;
  color: #0f172a;
}
.compare-price {
  text-decoration: line-through;
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
}
.save-pill {
  margin-left: auto;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ecfdf5;
  color: #065f46;
  font-size: 13px;
  font-weight: 600;
}`,
  mobile: `.price-container{
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.price {
  font-weight: 700;
  font-size: 22px;
  color: #0f172a;
}
.compare-price {
  text-decoration: line-through;
  color: #64748b;
  font-size: 14px;
}
.save-pill {
  padding: 4px 10px;
  border-radius: 999px;
  background: #ecfdf5;
  color: #065f46;
  font-size: 12px;
  font-weight: 600;
}`,
};
